import { useEffect, useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";
import { typefaceFonts } from "@stackje/site-builder/type-pairings";
import { TYPEFACE_IDS, type BrandFields, type BrandFontChoice, type BrandVoice, type OwnerCommand, type Typeface, type Workspace } from "../api/contract";
import { FONT_SUGGESTIONS, fontFamilyCss, fontPreviewHref, googleFontExists, isFontName } from "../lib/fonts";
import { TYPEFACE_FONTS_HREF, TYPEFACE_SAMPLES } from "../lib/typefaces";
import { Spinner } from "./Spinner";

const HEX = /^#[0-9a-fA-F]{6}$/;
const MAX_SUPPORTING = 4;

interface Form {
  accent: string;
  palette: string[];
  typeface: Typeface | null;
  /** The actual families; a style card fills them from its first pairing. */
  fonts: BrandFontChoice | null;
  voice: Required<BrandVoice>;
}

/** Whether Google Fonts has each family the owner typed. */
type FontStatus = "checking" | "found" | "missing";

function formFrom(brand: BrandFields | null | undefined, look: Workspace["look"]): Form {
  return {
    accent: brand?.accent ?? look?.accent ?? "#dd5304",
    palette: brand?.palette ?? [],
    typeface: brand?.typeface ?? look?.typeface ?? null,
    fonts: brand?.fonts ? { display: brand.fonts.display, body: brand.fonts.body } : null,
    voice: { tone: "", audience: "", write: "", avoid: "", ...brand?.voice },
  };
}

function voiceOf(form: Form): BrandVoice | null {
  const voice = Object.fromEntries(Object.entries(form.voice).map(([key, value]) => [key, value.trim()]).filter(([, value]) => value)) as Partial<BrandVoice>;
  return voice.tone ? voice as BrandVoice : null;
}

/** Only what changed, so the server keeps everything else as it is. */
export function brandChanges(before: BrandFields | null | undefined, form: Form): Extract<OwnerCommand, { kind: "brand.set" }> | null {
  const command: Extract<OwnerCommand, { kind: "brand.set" }> = { kind: "brand.set" };
  if (!before || form.accent.toLowerCase() !== before.accent.toLowerCase()) command.accent = form.accent.toLowerCase();
  const palette = form.palette.map((colour) => colour.toLowerCase());
  if (JSON.stringify(palette) !== JSON.stringify(before?.palette ?? [])) command.palette = palette;
  if ((form.typeface ?? undefined) !== before?.typeface) command.typeface = form.typeface;
  const fonts = form.fonts ? { display: form.fonts.display.trim(), body: form.fonts.body.trim() } : null;
  if (fonts?.display !== before?.fonts?.display || fonts?.body !== before?.fonts?.body) command.fonts = fonts;
  const voice = voiceOf(form);
  if (JSON.stringify(voice ?? null) !== JSON.stringify(before?.voice ?? null)) command.voice = voice;
  return Object.keys(command).length > 1 ? command : null;
}

interface BrandViewProps {
  workspace: Workspace;
  /** Saves a change to the guidelines; resolves with whether it applied. */
  onSave: (command: Extract<OwnerCommand, { kind: "brand.set" }>) => Promise<boolean>;
  /** Hands a request to the admin in the chat. */
  onAsk: (text: string) => void;
}

/** The owner's brand guidelines: how the brand writes, its colours and its type. */
export function BrandView({ workspace, onSave, onAsk }: BrandViewProps) {
  const brand = workspace.brand ?? null;
  const [editing, setEditing] = useState(brand !== null);
  const [form, setForm] = useState(() => formFrom(brand, workspace.look));
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "failed">("idle");

  // Guidelines changed elsewhere (the admin, or Undo) replace the form unless the owner is mid-edit.
  const brandKey = JSON.stringify(brand);
  useEffect(() => {
    setForm(formFrom(brand, workspace.look));
    if (brand) setEditing(true);
  }, [brandKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (document.querySelector("link[data-bw-typefaces]")) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = TYPEFACE_FONTS_HREF;
    link.dataset.bwTypefaces = "";
    document.head.append(link);
  }, []);

  // Each family the owner types is checked with Google Fonts (after a pause) and loaded to preview it.
  const [fontStatus, setFontStatus] = useState<Record<string, FontStatus>>({});
  const [choosingFonts, setChoosingFonts] = useState(false);
  const families = form.fonts ? [...new Set([form.fonts.display.trim(), form.fonts.body.trim()])].filter(isFontName) : [];
  const familiesKey = families.join("|");
  useEffect(() => {
    const unchecked = families.filter((family) => !fontStatus[family]);
    if (!unchecked.length) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setFontStatus((current) => ({ ...current, ...Object.fromEntries(unchecked.map((family) => [family, "checking" as const])) }));
      void Promise.all(unchecked.map(async (family) => [family, await googleFontExists(family) ? "found" : "missing"] as const)).then((checked) => {
        if (!cancelled) setFontStatus((current) => ({ ...current, ...Object.fromEntries(checked) }));
      });
    }, 400);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [familiesKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const found = families.filter((family) => fontStatus[family] === "found");
  const foundKey = found.join("|");
  useEffect(() => {
    if (!found.length) return;
    let link = document.querySelector<HTMLLinkElement>("link[data-bw-brand-fonts]");
    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.dataset.bwBrandFonts = "";
      document.head.append(link);
    }
    link.href = fontPreviewHref(found);
  }, [foundKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fontProblem = !form.fonts ? null
    : !isFontName(form.fonts.display) || !isFontName(form.fonts.body) ? "Enter a font name for both headings and body text."
      : families.find((family) => fontStatus[family] === "missing") ? `We can’t find “${families.find((family) => fontStatus[family] === "missing")}” on Google Fonts. Check the spelling and capitals.`
        : null;
  const fontsChecking = families.some((family) => fontStatus[family] !== "found" && fontStatus[family] !== "missing");
  const changes = brandChanges(brand, form);
  const valid = HEX.test(form.accent) && form.palette.every((colour) => HEX.test(colour)) && !fontProblem && !(changes?.fonts && fontsChecking);
  const pickStyle = (id: Typeface) => setForm({ ...form, typeface: form.typeface === id ? null : id, fonts: form.typeface === id ? null : typefaceFonts(id) });
  const setFont = (key: keyof BrandFontChoice, value: string) => {
    const fonts = { display: form.fonts?.display ?? "", body: form.fonts?.body ?? "", [key]: value };
    // Fonts of their own aren't one of the styles any more.
    setForm({ ...form, fonts, typeface: TYPEFACE_IDS.find((id) => JSON.stringify(typefaceFonts(id)) === JSON.stringify({ display: fonts.display.trim(), body: fonts.body.trim() })) ?? null });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!changes || !valid) return;
    setStatus("saving");
    setStatus(await onSave(changes) ? "saved" : "failed");
  };

  if (!editing) {
    return (
      <div className="bw-brand">
        <div className="bw-brand-empty">
          <h3>No brand guidelines yet</h3>
          <p>They keep every design, and everything Stax writes, consistent: how you sound, your colours and your type.</p>
          <div className="bw-actions">
            <button type="button" className="bw-btn bw-btn--primary" onClick={() => onAsk("Suggest brand guidelines for my business")}>Suggest them for me</button>
            <button type="button" className="bw-btn bw-btn--quiet" onClick={() => setEditing(true)}>Set them myself</button>
          </div>
        </div>
      </div>
    );
  }

  const setVoice = (key: keyof BrandVoice, value: string) => setForm({ ...form, voice: { ...form.voice, [key]: value } });
  const setPalette = (index: number, value: string) => setForm({ ...form, palette: form.palette.map((colour, at) => at === index ? value : colour) });

  return (
    <form className="bw-brand" onSubmit={(event) => void submit(event)}>
      <p className="bw-library-intro">Every design and everything Stax writes follows these.</p>

      <section aria-labelledby="bw-brand-voice">
        <h3 id="bw-brand-voice" className="bw-library-title">Language style</h3>
        <div className="bw-item-edit">
          <label>
            <span>Tone</span>
            <input value={form.voice.tone} placeholder="e.g. Warm, homely and reassuring" maxLength={200} onChange={(event) => setVoice("tone", event.target.value)} />
          </label>
          <label>
            <span>Speaks to</span>
            <input value={form.voice.audience} placeholder="e.g. Families planning a celebration" maxLength={400} onChange={(event) => setVoice("audience", event.target.value)} />
          </label>
          <label>
            <span>How to write</span>
            <textarea rows={2} value={form.voice.write} placeholder="e.g. Short sentences, first person, friendly" maxLength={400} onChange={(event) => setVoice("write", event.target.value)} />
          </label>
          <label>
            <span>Avoid</span>
            <textarea rows={2} value={form.voice.avoid} placeholder="e.g. Hard selling, jargon" maxLength={400} onChange={(event) => setVoice("avoid", event.target.value)} />
          </label>
          {!form.voice.tone.trim() && Object.values(form.voice).some((value) => value.trim()) && (
            <p className="bw-item-note">Add a tone to keep the rest of your language style.</p>
          )}
        </div>
      </section>

      <section aria-labelledby="bw-brand-colours">
        <h3 id="bw-brand-colours" className="bw-library-title">Colours</h3>
        <div className="bw-colour">
          <input type="color" aria-label="Main colour" value={HEX.test(form.accent) ? form.accent : "#000000"} onChange={(event) => setForm({ ...form, accent: event.target.value })} />
          <label>
            <span>Main colour</span>
            <input value={form.accent} aria-invalid={!HEX.test(form.accent) || undefined} onChange={(event) => setForm({ ...form, accent: event.target.value.trim() })} />
          </label>
        </div>
        {form.palette.map((colour, index) => (
          <div className="bw-colour" key={index}>
            <input type="color" aria-label={`Supporting colour ${index + 1}`} value={HEX.test(colour) ? colour : "#000000"} onChange={(event) => setPalette(index, event.target.value)} />
            <label>
              <span>Supporting colour</span>
              <input value={colour} aria-invalid={!HEX.test(colour) || undefined} onChange={(event) => setPalette(index, event.target.value.trim())} />
            </label>
            <button type="button" className="bw-btn bw-btn--icon" aria-label={`Remove supporting colour ${colour}`} onClick={() => setForm({ ...form, palette: form.palette.filter((_, at) => at !== index) })}>
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        ))}
        {form.palette.length < MAX_SUPPORTING && (
          <button type="button" className="bw-btn bw-btn--text bw-add" onClick={() => setForm({ ...form, palette: [...form.palette, "#f4f4f5"] })}>
            <Plus size={16} aria-hidden="true" /> Add a supporting colour
          </button>
        )}
        {!valid && <p className="bw-item-note" role="alert">Colours need to be written like #a4481f.</p>}
      </section>

      <section aria-labelledby="bw-brand-type">
        <h3 id="bw-brand-type" className="bw-library-title">Typography</h3>
        {form.fonts && isFontName(form.fonts.display) && isFontName(form.fonts.body) && (
          <div className="bw-font-now" aria-live="polite">
            <strong style={{ fontFamily: fontFamilyCss(form.fonts.display) }}>{form.fonts.display}</strong>
            <span style={{ fontFamily: fontFamilyCss(form.fonts.body) }}>
              {form.fonts.display.trim() === form.fonts.body.trim() ? "for headings and text" : `for headings, with ${form.fonts.body.trim()} for text`}
            </span>
          </div>
        )}
        <div className="bw-typefaces" role="radiogroup" aria-labelledby="bw-brand-type">
          {TYPEFACE_IDS.map((id) => {
            const sample = TYPEFACE_SAMPLES[id];
            const fonts = typefaceFonts(id);
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={form.typeface === id}
                className="bw-typeface"
                onClick={() => pickStyle(id)}
              >
                <span className="bw-typeface-sample" style={{ fontFamily: sample.display, fontWeight: sample.weight }}>Aa</span>
                <span>
                  <strong style={{ fontFamily: sample.display, fontWeight: sample.weight }}>{sample.name}</strong>
                  <span style={{ fontFamily: sample.body }}>{fonts.display === fonts.body ? fonts.display : `${fonts.display} + ${fonts.body}`}</span>
                </span>
              </button>
            );
          })}
        </div>
        <button type="button" className="bw-btn bw-btn--text" aria-expanded={choosingFonts || undefined} onClick={() => setChoosingFonts(!choosingFonts)}>
          {choosingFonts ? "Hide font choice" : "Choose your own fonts"}
        </button>
        {choosingFonts && (
          <div className="bw-item-edit bw-font-choice">
            <p className="bw-item-note">Any font from fonts.google.com, spelled as it shows there.</p>
            <label>
              <span>Headings</span>
              <input list="bw-font-suggestions" value={form.fonts?.display ?? ""} placeholder="e.g. Young Serif" maxLength={60} aria-invalid={Boolean(form.fonts && fontStatus[form.fonts.display.trim()] === "missing") || undefined} onChange={(event) => setFont("display", event.target.value)} />
            </label>
            <label>
              <span>Body text</span>
              <input list="bw-font-suggestions" value={form.fonts?.body ?? ""} placeholder="e.g. Figtree" maxLength={60} aria-invalid={Boolean(form.fonts && fontStatus[form.fonts.body.trim()] === "missing") || undefined} onChange={(event) => setFont("body", event.target.value)} />
            </label>
            <datalist id="bw-font-suggestions">
              {FONT_SUGGESTIONS.map((family) => <option key={family} value={family} />)}
            </datalist>
            {form.fonts && (
              <button type="button" className="bw-btn bw-btn--text" onClick={() => setForm({ ...form, fonts: null, typeface: null })}>Clear fonts</button>
            )}
          </div>
        )}
        {fontProblem && <p className="bw-item-note" role="alert">{fontProblem}</p>}
      </section>

      <div className="bw-brand-save">
        {status === "saved" && !changes && (
          <div className="bw-callout" data-tone="done" role="status">
            <p>Saved. New designs and writing follow these.</p>
            <button type="button" className="bw-btn bw-btn--quiet" onClick={() => onAsk("Update my website to follow my brand guidelines")}>Update my website to match</button>
          </div>
        )}
        {status === "failed" && <p className="bw-item-note" role="alert">That didn’t save. Try again.</p>}
        <div className="bw-actions">
          <button type="submit" className="bw-btn bw-btn--primary" disabled={!changes || !valid || status === "saving"}>
            {status === "saving" && <Spinner />}
            {status === "saving" ? "Saving…" : "Save guidelines"}
          </button>
          <button type="button" className="bw-btn bw-btn--text" onClick={() => onAsk("Suggest changes to my brand guidelines")}>Ask Stax for ideas</button>
        </div>
      </div>
    </form>
  );
}
